import {
  type GachaEnvironment,
  type GachaNetworkId,
  getGachaEvmChains,
} from "@anaconda/core/gacha/evm-chains"
import { action, atom, computed } from "@reatom/core"

export function createGachaNetworkSelection(name = "gacha.network") {
  const environment = atom<GachaEnvironment | null>(null, `${name}.environment`)
  const selected = atom<GachaNetworkId>("solana", `${name}.selected`)
  // Only the foreground purchase/signature queue owns this lock, never recovery.
  const purchasePending = atom(false, `${name}.purchasePending`)
  const locked = computed(() => purchasePending(), `${name}.locked`)
  let initialized = false
  const initialize = action(
    (
      next: GachaEnvironment | null,
      activeChain: number | undefined,
      storage?: Pick<Storage, "getItem" | "setItem">,
      activeWalletReady = true,
    ) => {
      if (initialized && environment() === next) return
      if (locked()) return
      initialized = activeWalletReady
      environment.set(next)
      const chains = next ? getGachaEvmChains(next) : []
      let saved: string | null = null
      try {
        saved = next
          ? (storage?.getItem(`gacha:network:${next}`) ?? null)
          : null
      } catch {
        /* Storage is optional. */
      }
      const restored = chains.find(
        (chain) => String(chain.chainId) === saved,
      )?.chainId
      if (saved === "solana" || restored) initialized = true
      const active = activeWalletReady
        ? chains.find((chain) => chain.chainId === activeChain)?.chainId
        : undefined
      selected.set(
        saved === "solana" ? "solana" : (restored ?? active ?? "solana"),
      )
    },
    `${name}.initialize`,
  )
  const select = action(
    (next: GachaNetworkId, storage?: Pick<Storage, "setItem">) => {
      if (locked()) return false
      const currentEnvironment = environment()
      if (
        next !== "solana" &&
        (!currentEnvironment ||
          !getGachaEvmChains(currentEnvironment).some(
            (chain) => chain.chainId === next,
          ))
      )
        return false
      selected.set(next)
      initialized = true
      try {
        if (currentEnvironment)
          storage?.setItem(`gacha:network:${currentEnvironment}`, String(next))
      } catch {
        /* A blocked store does not invalidate the browsing preference. */
      }
      return true
    },
    `${name}.select`,
  )
  return {
    environment,
    selected,
    purchasePending,
    locked,
    initialize,
    select,
  }
}
export type GachaNetworkSelection = ReturnType<
  typeof createGachaNetworkSelection
>
export const gachaNetworkSelection = createGachaNetworkSelection()

export function getGachaNetworkStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage
  } catch {
    return undefined
  }
}
